import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertTriangle,
  BellElectric,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileText,
  Filter,
  Megaphone,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Trash2,
  TrafficCone,
  Wrench,
  X,
  type LucideIcon
} from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { useAppHeader, type AppHeaderConfig } from "@/components/app-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { FilterChip } from "@/components/ui/filter-chip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListSearch, ListToolbar } from "@/components/ui/list-toolbar";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { AdminUpdate } from "./AdminUpdate";
import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import useAdminUpdates from "../../../hooks/admin/useManageAdminUpdates";

const UPDATES_HEADER: AppHeaderConfig = {
  crumbs: [
    { id: "internal-tools", label: "Internal Tools", icon: Wrench },
    {
      id: "announcements",
      label: "Announcements",
      current: true,
      icon: Megaphone
    }
  ]
};

type StatusFilter = "active" | "archived";

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" }
];

const statusFilterLabel = (value: StatusFilter): string =>
  STATUS_OPTIONS.find((o) => o.value === value)?.label ?? "Active";

const toMs = (value?: AdminUpdate["createdAt"]): number => {
  const d = value?.toDate?.();
  return d ? d.getTime() : 0;
};

const formatCreatedDate = (value?: AdminUpdate["createdAt"]): string => {
  const d = value?.toDate?.();
  if (!d) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short"
  });
};

// Tones mirror the app announcement strip (see layout/AnnouncementStrip).
const PRIORITY_DISPLAY: Record<
  AdminUpdate["priority"],
  { label: string; tone: StatusTone; icon: LucideIcon; preview: string }
> = {
  low: {
    label: "Low",
    tone: "neutral",
    icon: Megaphone,
    preview: "bg-fill-1 text-fg-2"
  },
  medium: {
    label: "Medium",
    tone: "orange",
    icon: BellElectric,
    preview: "bg-orange-soft text-orange"
  },
  high: {
    label: "High",
    tone: "orange",
    icon: TrafficCone,
    preview: "bg-orange-soft text-orange"
  },
  critical: {
    label: "Critical",
    tone: "danger",
    icon: AlertTriangle,
    preview: "bg-danger-soft text-danger"
  }
};

const priorityDisplay = (priority: AdminUpdate["priority"]) =>
  PRIORITY_DISPLAY[priority] ?? {
    label: "—",
    tone: "neutral" as StatusTone,
    icon: FileText,
    preview: "bg-fill-1 text-fg-2"
  };

const PriorityPill = ({ priority }: { priority: AdminUpdate["priority"] }) => {
  const p = priorityDisplay(priority);
  return (
    <StatusBadge tone={p.tone} dot>
      {p.label}
    </StatusBadge>
  );
};

const statusBadgeProps = (
  status: AdminUpdate["status"]
): { tone: StatusTone; label: string; icon?: LucideIcon } => {
  switch (status) {
    case "active":
      return { tone: "success", label: "Active", icon: Check };
    case "inactive":
      return { tone: "neutral", label: "Inactive", icon: Clock };
    case "resolved":
      return { tone: "neutral", label: "Resolved", icon: CheckCircle2 };
    default:
      return { tone: "neutral", label: "—" };
  }
};

const AnnouncementPreview = ({ update }: { update: AdminUpdate }) => {
  const p = priorityDisplay(update.priority);
  const Icon = p.icon;
  return (
    <div
      className={cn("flex w-full items-start gap-3 rounded-lg p-3", p.preview)}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex w-full items-start justify-between gap-2">
          <span className="font-medium text-fg">
            {update.title?.trim() || "Untitled announcement"}
          </span>
          <PriorityPill priority={update.priority} />
        </div>
        <span className="line-clamp-2 text-sm text-fg-3">
          {update.description?.trim() || "—"}
        </span>
      </div>
    </div>
  );
};

const AdminUpdatesManager = () => {
  useAppHeader(UPDATES_HEADER);
  const {
    adminUpdates,
    createAdminUpdate,
    updateAdminUpdate,
    deleteAdminUpdate,
    isLoading,
    error,
    fetchAdminUpdates
  } = useAdminUpdates();

  useEffect(() => {
    fetchAdminUpdates({ isAdmin: true });
  }, []);

  const { firstName, lastName } = useSelector(lawyerSelectors.selectAll);

  const author = `${firstName ?? ""} ${lastName ?? ""}`.trim();

  const getInitialAdminUpdate = (): AdminUpdate => ({
    id: undefined,
    title: "",
    description: "",
    status: "active",
    priority: "medium",
    author
  });

  const [currentAdminUpdate, setCurrentAdminUpdate] = useState<
    Partial<AdminUpdate>
  >(() => getInitialAdminUpdate());

  const [viewArchivedOnly, setViewArchivedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);

  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [pendingResolve, setPendingResolve] = useState<AdminUpdate | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminUpdate | null>(null);

  const titleId = useId();
  const descriptionId = useId();
  const priorityId = useId();

  const handleCreateOrUpdateAdminUpdate = () => {
    const initialAdminUpdateState = getInitialAdminUpdate();
    const updateDetails: AdminUpdate = {
      ...initialAdminUpdateState,
      ...currentAdminUpdate,
      author: currentAdminUpdate.author || author
    };

    if (currentAdminUpdate.id) {
      updateAdminUpdate(currentAdminUpdate.id, updateDetails);
      toast.success("Admin update updated.", { duration: 5000 });
    } else {
      createAdminUpdate(updateDetails);
      toast.success("Admin update created.", { duration: 5000 });
    }

    setCurrentAdminUpdate(initialAdminUpdateState);
    setViewArchivedOnly(false);
    setEditorOpen(false);
  };

  const openCreateEditor = () => {
    setCurrentAdminUpdate(getInitialAdminUpdate());
    setViewArchivedOnly(false);
    setEditorOpen(true);
  };

  const openEditEditor = (adminUpdate: AdminUpdate) => {
    setCurrentAdminUpdate(adminUpdate);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setCurrentAdminUpdate(getInitialAdminUpdate());
  };

  const openResolveDialog = (u: AdminUpdate) => {
    setPendingResolve(u);
    setResolveDialogOpen(true);
  };

  const confirmResolve = () => {
    if (!pendingResolve?.id) {
      setResolveDialogOpen(false);
      setPendingResolve(null);
      return;
    }
    updateAdminUpdate(pendingResolve.id, { status: "resolved" });
    setResolveDialogOpen(false);
    setPendingResolve(null);
  };

  const openDeleteDialog = (u: AdminUpdate) => {
    setPendingDelete(u);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!pendingDelete?.id) {
      setDeleteDialogOpen(false);
      setPendingDelete(null);
      return;
    }
    deleteAdminUpdate(pendingDelete.id);
    setDeleteDialogOpen(false);
    setPendingDelete(null);
  };

  const sortedUpdates = useMemo(() => {
    return [...adminUpdates].sort(
      (a, b) => toMs(b.createdAt) - toMs(a.createdAt)
    );
  }, [adminUpdates]);

  const visibleUpdates = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sortedUpdates
      .filter((u) =>
        viewArchivedOnly ? u.status === "resolved" : u.status !== "resolved"
      )
      .filter((u) => {
        if (!query) return true;
        return (
          (u.title ?? "").toLowerCase().includes(query) ||
          (u.description ?? "").toLowerCase().includes(query)
        );
      });
  }, [sortedUpdates, viewArchivedOnly, search]);

  const showActionsColumn = !viewArchivedOnly;
  const statusFilter: StatusFilter = viewArchivedOnly ? "archived" : "active";

  const handleStatusFilterChange = (value: StatusFilter) => {
    setViewArchivedOnly(value === "archived");
  };

  const columns: ColumnDef<AdminUpdate, unknown>[] = [
    {
      id: "title",
      header: "Title",
      cell: ({ row }) => (
        <div className="flex w-full flex-col items-start gap-0.5 py-2 text-left whitespace-normal">
          <span className="font-medium text-fg">{row.original.title}</span>
          <span className="text-sm whitespace-normal text-fg-3">
            {row.original.description}
          </span>
        </div>
      )
    },
    {
      id: "priority",
      header: "Priority",
      cell: ({ row }) => <PriorityPill priority={row.original.priority} />
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = statusBadgeProps(row.original.status);
        const StatusIcon = status.icon;
        return (
          <StatusBadge tone={status.tone}>
            {StatusIcon ? <StatusIcon /> : null}
            {status.label}
          </StatusBadge>
        );
      }
    },
    {
      id: "createdAt",
      header: "Date Created",
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-fg-3">
          {formatCreatedDate(row.original.createdAt)}
        </span>
      )
    },
    ...(showActionsColumn
      ? [
          {
            id: "actions",
            header: "",
            cell: ({ row }) => {
              const u = row.original;
              return (
                <div className="flex items-center justify-end gap-1.5">
                  <Button size="sm" onClick={() => openEditEditor(u)}>
                    Edit
                  </Button>
                  {u.status !== "resolved" && u.id ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openResolveDialog(u)}
                    >
                      Resolve
                    </Button>
                  ) : null}
                  {u.id ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="More actions"
                        >
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => openDeleteDialog(u)}
                        >
                          <Trash2 />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              );
            }
          } satisfies ColumnDef<AdminUpdate, unknown>
        ]
      : [])
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Announcements"
          count={isLoading ? null : visibleUpdates.length}
          actions={
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Refresh"
                onClick={() => fetchAdminUpdates({ isAdmin: true })}
              >
                <RefreshCw />
              </Button>
              <Button variant="primary" size="sm" onClick={openCreateEditor}>
                <Plus /> Create Announcement
              </Button>
            </>
          }
        />
        <ListToolbar>
          <ListSearch
            value={search}
            onValueChange={setSearch}
            placeholder="Search announcements..."
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <FilterChip
                active={viewArchivedOnly}
                aria-label="Filter announcements by status"
              >
                <Filter />
                <span>Status</span>
                <span className="text-fg">
                  {statusFilterLabel(statusFilter)}
                </span>
                <ChevronDown className="text-fg-4" />
              </FilterChip>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-55">
              <DropdownMenuRadioGroup
                value={statusFilter}
                onValueChange={(value) =>
                  handleStatusFilterChange(value as StatusFilter)
                }
              >
                {STATUS_OPTIONS.map(({ value, label }) => (
                  <DropdownMenuRadioItem key={value} value={value}>
                    {label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          {error ? (
            <span className="text-sm text-danger">Error: {error}</span>
          ) : null}
        </ListToolbar>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {!isLoading && visibleUpdates.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title={
              viewArchivedOnly
                ? "No Archived Announcements"
                : "No Active Announcements"
            }
            className="py-16"
          />
        ) : (
          <DataTable
            data={visibleUpdates}
            columns={columns}
            getRowId={(u, i) => u.id ?? `${u.title}-${toMs(u.createdAt)}-${i}`}
            isLoading={isLoading}
            rowHeight="double"
            className="overflow-visible"
          />
        )}
      </div>

      <AlertDialog
        open={resolveDialogOpen}
        onOpenChange={(next) => {
          setResolveDialogOpen(next);
          if (!next) setPendingResolve(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resolve Announcement</AlertDialogTitle>
            <AlertDialogDescription>
              Users will no longer see this as an active system announcement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingResolve ? (
            <AnnouncementPreview update={pendingResolve} />
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel size="sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="primary"
              size="sm"
              onClick={confirmResolve}
            >
              <CheckCircle2 />
              Resolve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(next) => {
          setDeleteDialogOpen(next);
          if (!next) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Announcement</AlertDialogTitle>
            <AlertDialogDescription>
              This action can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingDelete ? (
            <AnnouncementPreview update={pendingDelete} />
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel size="sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              size="sm"
              onClick={confirmDelete}
            >
              <Trash2 />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet
        open={editorOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeEditor();
        }}
      >
        <SheetContent
          side="right"
          showCloseButton={false}
          className="w-full gap-0 sm:max-w-105"
        >
          <div className="flex items-start justify-between gap-3 border-b border-line-2">
            <SheetHeader className="min-w-0">
              <SheetTitle className="truncate">
                {currentAdminUpdate.id
                  ? "Edit Announcement"
                  : "Create Announcement"}
              </SheetTitle>
              <SheetDescription className="truncate text-sm">
                {currentAdminUpdate.id
                  ? `Originally created at ${formatCreatedDate(
                      currentAdminUpdate.createdAt
                    )}`
                  : "Manage system announcements and notifications"}
              </SheetDescription>
            </SheetHeader>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close editor"
              className="mt-3 mr-3"
              onClick={closeEditor}
            >
              <X />
            </Button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={titleId}>Title</Label>
              <Input
                id={titleId}
                placeholder="Enter announcement title"
                value={currentAdminUpdate.title ?? ""}
                onChange={(event) =>
                  setCurrentAdminUpdate((prev) => ({
                    ...prev,
                    title: event.target.value
                  }))
                }
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={descriptionId}>Description</Label>
              <Textarea
                id={descriptionId}
                className="min-h-24"
                placeholder="Enter announcement description"
                value={currentAdminUpdate.description ?? ""}
                onChange={(event) =>
                  setCurrentAdminUpdate((prev) => ({
                    ...prev,
                    description: event.target.value
                  }))
                }
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={priorityId}>Priority</Label>
              <Select
                value={currentAdminUpdate.priority ?? "medium"}
                onValueChange={(value) =>
                  setCurrentAdminUpdate((prev) => ({
                    ...prev,
                    priority: value as AdminUpdate["priority"]
                  }))
                }
              >
                <SelectTrigger id={priorityId} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium" className="text-orange">
                    Medium
                  </SelectItem>
                  <SelectItem value="high" className="text-orange">
                    High
                  </SelectItem>
                  <SelectItem value="critical" className="text-danger">
                    Critical
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex w-full items-center justify-end pt-2">
              <Button
                variant="primary"
                size="sm"
                disabled={isLoading}
                onClick={handleCreateOrUpdateAdminUpdate}
              >
                {isLoading ? <Spinner /> : <Megaphone />}
                {currentAdminUpdate.id
                  ? "Update Admin Update"
                  : "Publish Announcement"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminUpdatesManager;
